import React, { useEffect, useRef, useState, useCallback } from 'react';
import { pbAuthedFetch } from "../authHeaders";
import { useOverlay } from '@/hooks/useOverlay';

// ── Types ────────────────────────────────────────────────────────────────────

interface StellaAction {
  label: string;
  kind?: 'primary' | 'gold' | '';
  onClick: () => void;
}

interface StellaMessage {
  role: 'user' | 'assistant';
  html: string;
  cursor?: boolean;
  actions?: StellaAction[] | null;
}

interface StellaRespondResult {
  text: string;
  actions: StellaAction[];
}

interface ProjectRecord {
  id: string;
  name: string;
  status: string;
  area: { label: string };
  owner: string;
  desc: string;
  pill: { label: string };
  stats: { Jira: number; Tasks: number; Risks: number };
}

interface JiraTicket {
  id: string;
  summary: string;
  type: string;
  priority: string;
  status: string;
  assignee: string;
}

interface TaskRecord {
  id: string;
  title: string;
  due: string;
  priority: string;
}

interface RiskRecord {
  id?: string;
  t: string;
  l: number;
  i: number;
  catL?: string;
  owner?: string;
}

interface FoundEntity {
  kind: 'project' | 'jira' | 'task' | 'risk' | 'missing';
  obj?: ProjectRecord | JiraTicket | TaskRecord | (RiskRecord & { id: string });
  id?: string;
  what?: string;
}

interface StellaPanelProps {
  open: boolean;
  onClose: () => void;
}

// ── Window globals augmentation ──────────────────────────────────────────────

declare global {
  interface Window {
    RISKS?: Record<string, RiskRecord>;
    WS_JIRA_TICKETS?: JiraTicket[];
    WS_TASKS?: TaskRecord[];
    stellaOpenEntity?: (kind: string, id: string) => void;
    askStella?: (prompt: string) => void;
    stellaAsk?: (prompt: string) => void;
    chatPrefill?: (text: string) => void;
    chatClear?: () => void;
    chatSend?: () => void;
    chatGenerateExampleTicket?: () => void;
    showPage?: (id: string) => void;
    pbToast?: (msg: string, extra: string) => void;
    triageOpenCenter?: () => void;
    triageEmail?: (k: string) => void;
    pastWorkSearchOpen?: () => void;
    ticketCreateOpen?: (mode?: string, origin?: string) => void;
    projectDrillDown?: (id: string) => void;
    projectDrillTab?: (tab: string) => void;
    oralsOpenGeneratedItem?: (kind: string, id: string) => void;
    pastWorkView?: (id: string) => void;
    projViewTaggedEmail?: (id: string) => void;
    appendActivityRow?: (row: Record<string, unknown>) => void;
    closeMo?: () => void;
  }
}

// ── Constants ────────────────────────────────────────────────────────────────

const WELCOME = `Good morning. I'm <strong>Stella</strong> — your Atlas AI assistant. I can answer questions about your infrastructure, look up tickets and tasks, recommend next actions, and help investigate incidents.<br><br><em>Try:</em> <em>"What are my top risks?"</em>, <em>"Tell me about a CI source,"</em> or <em>"What changed in the last 7 days?"</em>`;

const SUGGESTIONS: [string, string[]][] = [
  ['Infrastructure & sources', ['What sources are registered?', 'Show me CI conflicts', 'What changed in the last 7 days?', 'Which sources are stale?']],
  ['Triage & inbox', ['What needs attention?', 'Show me reconciliation findings', 'Any unowned CIs?']],
  ['Create & act', ['Help me investigate an outage', 'Show cost savings opportunities', 'Open settings']],
  ['Registry & assets', ['Show me the CI registry', 'How many assets are tracked?', 'Any accountability gaps?']],
  ['Reports & analytics', ['Generate a status summary', 'Show me the AD savings report', 'What are the top outages?']],
  ['Help & meta', ['What can you help me with?', 'How does the audit trail work?']],
  ['Analytics', ['What KPIs are off target?', 'Show me the anomaly signals', 'How do we compare to peer benchmarks?']],
  ['Taskers', ['What taskers are overdue?', 'What should I route today?']],
  ['ServiceNow Feed', ['What change requests need CAB approval?', 'Show me the open incident queue']],
  ['Capacity & migration', ['How many devices can we migrate next week without breaking the service desk?', 'What does the current wave cost us in Tier 1 utilization?']],
];

const QUICK_SUGGESTIONS = SUGGESTIONS.map(([, items]) => items[0]);

const STELLA_HISTORY: { role: string; content: string }[] = [];

const STELLA_LIVE_SYSTEM = "You are Stella, the Atlas AI assistant inside a federal IT operations console. Answer helpfully and concisely — a short paragraph or a few short bullets. You do NOT have live access to the infrastructure database in this chat, so never invent specific asset, source, or financial figures; if asked about a specific record, ask the user to reference it by ID. Plain text only, no markdown headings.";

// ── Helpers ──────────────────────────────────────────────────────────────────

const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

const esc = (s: string): string =>
  String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] || c));

function mdLite(s: string): string {
  return esc(s)
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[\s(])\*([^*\n]+)\*(?=[\s.,;:!?)]|$)/g, '$1<em>$2</em>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\n/g, '<br>');
}

const risksNow = (): Record<string, RiskRecord> =>
  (typeof window !== 'undefined' && window.RISKS) || {};

const chip = (kind: string, id: string): string =>
  `<span class="stella-entity" onclick="stellaOpenEntity('${kind}','${esc(id)}')">${esc(id)}</span>`;

function tokenize(html: string): { type: 'text' | 'tag'; value: string }[] {
  const tokens: { type: 'text' | 'tag'; value: string }[] = [];
  let i = 0;
  while (i < html.length) {
    if (html[i] === '<') {
      const end = html.indexOf('>', i);
      if (end < 0) { tokens.push({ type: 'text', value: html.substring(i) }); break; }
      tokens.push({ type: 'tag', value: html.substring(i, end + 1) }); i = end + 1;
    } else {
      const next = html.indexOf('<', i);
      if (next < 0) { tokens.push({ type: 'text', value: html.substring(i) }); break; }
      tokens.push({ type: 'text', value: html.substring(i, next) }); i = next;
    }
  }
  return tokens;
}

// ── Navigation bridges ───────────────────────────────────────────────────────

const winFn = <K extends keyof Window>(name: K): Window[K] | null =>
  (typeof window !== 'undefined' && typeof window[name] === 'function') ? window[name] : null;

const goPage = (id: string): void => { const f = winFn('showPage'); if (f) (f as (id: string) => void)(id); };
const notWired = (what: string): void => { const f = winFn('pbToast'); if (f) (f as (msg: string, extra: string) => void)(what + ' isn\'t available in this build yet.', ''); };

function stellaOpenEntity(kind: string, id: string): void {
  if (kind === 'project') {
    const dd = winFn('projectDrillDown');
    goPage('registry');
    if (dd) setTimeout(() => (dd as (id: string) => void)(id), 160);
  } else if (kind === 'jira') {
    const og = winFn('oralsOpenGeneratedItem');
    if (og) (og as (kind: string, id: string) => void)('jira', id); else goPage('reconciliation');
  } else if (kind === 'task') {
    const og = winFn('oralsOpenGeneratedItem');
    if (og) (og as (kind: string, id: string) => void)('task', id); else goPage('reconciliation');
  } else if (kind === 'risk') {
    const og = winFn('oralsOpenGeneratedItem');
    if (og) (og as (kind: string, id: string) => void)('risk', id); else goPage('reconciliation');
  } else if (kind === 'pastwork') {
    const f = winFn('pastWorkView');
    if (f) (f as (id: string) => void)(id); else notWired('The Related Work archive');
  } else if (kind === 'email') {
    const f = winFn('projViewTaggedEmail');
    if (f) (f as (id: string) => void)(id); else goPage('reconciliation');
  }
}

// ── Topic detection ──────────────────────────────────────────────────────────

function stellaTopic(query: string): string {
  const q = query.toLowerCase();
  if (q.match(/^(hi|hello|hey|what's up|good morning|good afternoon)\b/)) return 'greeting';
  if (q.match(/help|what can you do|capabilities/)) return 'help';
  if (q.match(/audit trail|how does.*audit|trail work/)) return 'audit_trail';
  if (q.match(/source|ci.*regist|registered/)) return 'sources';
  if (q.match(/conflict|reconcil|finding/)) return 'reconciliation';
  if (q.match(/outage|incident|down|unavail/)) return 'outages';
  if (q.match(/cost.*sav|saving|waste|ad.*spend/)) return 'cost';
  if (q.match(/asset|accountab|gap/)) return 'assets';
  if (q.match(/top.*risk|biggest risk|highest priority|what.*worry|attention/)) return 'top_risks';
  if (q.match(/changed.*7 days|past week|recent activity|what.*new|stale/)) return 'recent';
  if (q.match(/unowned|orphan/)) return 'unowned';
  if (q.match(/status.*summar|weekly|report/)) return 'weekly';
  if (q.match(/setting|config/)) return 'settings';
  return 'general';
}

// ── Entity lookup ────────────────────────────────────────────────────────────

function findEntities(query: string): FoundEntity[] {
  const found: FoundEntity[] = [];
  (query.match(/PROJ-\d+/gi) || []).forEach((id) => {
    found.push({ kind: 'missing', id: id.toUpperCase(), what: 'project' });
  });
  (query.match(/ITPMS-(?:MIW-)?\d+/gi) || []).forEach((id) => {
    const arr = (typeof window !== 'undefined' && window.WS_JIRA_TICKETS) || [];
    const j = arr.find((x) => x.id === id.toUpperCase());
    found.push(j ? { kind: 'jira', obj: j } : { kind: 'missing', id: id.toUpperCase(), what: 'jira' });
  });
  (query.match(/TK-[A-Z0-9-]+/gi) || []).forEach((id) => {
    const arr = (typeof window !== 'undefined' && window.WS_TASKS) || [];
    const tk = arr.find((x) => x.id === id.toUpperCase());
    found.push(tk ? { kind: 'task', obj: tk } : { kind: 'missing', id: id.toUpperCase(), what: 'task' });
  });
  (query.match(/\bR-[A-Z0-9-]+/gi) || []).forEach((id) => {
    const r = risksNow()[id.toUpperCase()];
    found.push(r ? { kind: 'risk', obj: { id: id.toUpperCase(), ...r } } : { kind: 'missing', id: id.toUpperCase(), what: 'risk' });
  });
  return found;
}

// ── Severity label ───────────────────────────────────────────────────────────

function sevLabel(score: number): string {
  if (score >= 20) return 'Critical';
  if (score >= 12) return 'High';
  if (score >= 6) return 'Medium';
  return 'Low';
}

// ── Response engine ──────────────────────────────────────────────────────────

function respond(query: string): StellaRespondResult {
  const entities = findEntities(query);
  if (entities.length) {
    const parts: string[] = [];
    const actions: StellaAction[] = [];
    entities.forEach((e) => {
      if (e.kind === 'jira') {
        const j = e.obj as JiraTicket;
        parts.push('<strong>' + chip('jira', j.id) + '</strong> — ' + esc(j.summary) + '<br>Type: <em>' + esc(j.type) + '</em> · Priority: <em>' + esc(j.priority) + '</em> · Status: <em>' + esc(j.status) + '</em> · Assignee: <em>' + esc(j.assignee) + '</em>');
        actions.push({ label: 'Open ' + j.id, kind: 'primary', onClick: () => stellaOpenEntity('jira', j.id) });
      } else if (e.kind === 'task') {
        const tk = e.obj as TaskRecord;
        parts.push('<strong>' + chip('task', tk.id) + '</strong> — ' + esc(tk.title) + '<br>Due: <em>' + esc(tk.due) + '</em> · Priority: <em>' + esc(tk.priority) + '</em>');
        actions.push({ label: 'Open ' + tk.id, kind: 'primary', onClick: () => stellaOpenEntity('task', tk.id) });
      } else if (e.kind === 'risk') {
        const r = e.obj as RiskRecord & { id: string };
        const score = (r.l || 0) * (r.i || 0);
        parts.push(chip('risk', r.id) + ' — <strong>' + esc(r.t) + '</strong><br>Category: <em>' + esc(r.catL || '—') + '</em> · Likelihood ' + r.l + ' × Impact ' + r.i + ' = <strong>score ' + score + '</strong> (' + sevLabel(score) + ') · Owner: <em>' + esc(r.owner || '—') + '</em>');
        actions.push({ label: 'Open ' + r.id, kind: 'primary', onClick: () => stellaOpenEntity('risk', r.id) });
      } else {
        parts.push(`I don't have <strong>${esc(e.id || '')}</strong> in this workspace yet.`);
      }
    });
    return { text: parts.join('<br><br>'), actions };
  }

  const topic = stellaTopic(query);
  switch (topic) {
    case 'greeting':
      return {
        text: "Hi. I'm Stella — your Atlas AI assistant. I can answer questions about your infrastructure, look up tickets and tasks, recommend next actions, and investigate incidents. <strong>Try:</strong> <em>\"What sources are registered?\"</em> or <em>\"Show me reconciliation findings.\"</em>",
        actions: [],
      };

    case 'help':
      return {
        text: "Here's what I can do:<ul><li><strong>Investigate infrastructure</strong> — sources, CIs, conflicts, outages, accountability gaps.</li><li><strong>Look up items</strong> — paste an ID and I'll pull the full record.</li><li><strong>Query the portfolio</strong> — top risks, recent changes, stale sources, unowned assets.</li><li><strong>Surface reports</strong> — cost savings, AD savings, outage summaries, reconciliation findings.</li><li><strong>Open the right surface</strong> — registry, reconciliation, outages, settings.</li></ul>Every response is sourced from the common data model — audit-traceable.",
        actions: [],
      };

    case 'audit_trail':
      return {
        text: "Every Atlas action writes to the activity log with source attribution and timestamp. Created assertions, ingested sources, reconciliation findings — all linked to their provenance. That's the IG/GAO-ready record: one query → full provenance.",
        actions: [],
      };

    case 'top_risks': {
      const top = Object.entries(risksNow())
        .map(([id, r]) => ({ id, ...r, score: (r.l || 0) * (r.i || 0) }))
        .filter((r) => r.score)
        .sort((a, b) => b.score - a.score)
        .slice(0, 3);
      if (!top.length) return { text: "No risks currently in the register. I'll keep an eye on the inbox — if anything triages into a risk, I'll surface it here.", actions: [] };
      const items = top
        .map((r) => '<li>' + chip('risk', r.id) + ' <strong>' + esc(r.t) + '</strong> — <em>' + sevLabel(r.score) + ' (score ' + r.score + ')</em> · Owner: ' + esc(r.owner || '—') + '</li>')
        .join('');
      return { text: 'Top ' + top.length + ' risks by score:<ul>' + items + '</ul>Click any ID for the full record.', actions: [] };
    }

    case 'sources':
      return {
        text: "The CI registry tracks all ingested sources with their provenance, confidence, and conflict state. Open the Sources page to see every registered connector, or the Registry for the full CI inventory.",
        actions: [{ label: 'Open Sources', kind: 'primary', onClick: () => goPage('sources') }],
      };

    case 'reconciliation':
      return {
        text: "Reconciliation compares assertions across sources to find conflicts and gaps. Open the Reconciliation page to see findings, or ask me about specific conflicts.",
        actions: [{ label: 'Open Reconciliation', kind: 'primary', onClick: () => goPage('reconciliation') }],
      };

    case 'outages':
      return {
        text: "The Outages dashboard tracks service availability, noise events, and change-overlay correlations. Open it for the full picture.",
        actions: [{ label: 'Open Outages', kind: 'primary', onClick: () => goPage('outages') }],
      };

    case 'cost':
      return {
        text: "Cost savings and AD savings dashboards track spend efficiency. Open Cost Savings for AI-driven predictions, or AD Savings for directory hygiene metrics.",
        actions: [
          { label: 'Cost Savings', kind: 'primary', onClick: () => goPage('cost-savings') },
          { label: 'AD Savings', onClick: () => goPage('ad-waste') },
        ],
      };

    case 'assets':
      return {
        text: "The Assets page shows all tracked infrastructure with accountability mappings. Open it to see ownership gaps and unassigned CIs.",
        actions: [{ label: 'Open Assets', kind: 'primary', onClick: () => goPage('assets') }],
      };

    case 'unowned':
      return {
        text: "Unowned CIs are assets without a mapped owner — an accountability gap. Open the Assets page filtered to unowned, or check Discovery for new findings.",
        actions: [{ label: 'Open Assets', kind: 'primary', onClick: () => goPage('assets') }],
      };

    case 'recent':
      return {
        text: "Recent activity is tracked in the activity log with source attribution. Open the Overview for a summary of what changed, or check specific pages for detailed timelines.",
        actions: [{ label: 'Open Overview', kind: 'primary', onClick: () => goPage('') }],
      };

    case 'weekly':
      return {
        text: "I can summarize recent activity across your infrastructure. Check the Overview page for the current state, or ask about specific areas like outages, reconciliation, or cost savings.",
        actions: [{ label: 'Open Overview', kind: 'primary', onClick: () => goPage('') }],
      };

    case 'settings':
      return {
        text: "Settings lets you configure connectors, thresholds, and preferences.",
        actions: [{ label: 'Open Settings', kind: 'primary', onClick: () => goPage('settings') }],
      };

    default:
      return {
        text: "I can dig into that. Some places to start — paste any item ID for the full record, or ask things like <em>\"what sources are registered?\"</em>, <em>\"show me reconciliation findings\"</em>, or <em>\"what changed this week?\"</em>. I can also open the registry, reconciliation, or outages pages.",
        actions: [
          { label: 'Open Registry', kind: 'primary', onClick: () => goPage('registry') },
          { label: 'Reconciliation', onClick: () => goPage('reconciliation') },
          { label: 'Outages', onClick: () => goPage('outages') },
        ],
      };
  }
}

// ── Component ────────────────────────────────────────────────────────────────

export function StellaPanel({ open, onClose }: StellaPanelProps) {
  const [messages, setMessages] = useState<StellaMessage[]>([]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [busy, setBusy] = useState(false);
  const [allSugs, setAllSugs] = useState(false);
  const streamRef = useRef(false);
  const msgsRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const sendRef = useRef<((raw?: string) => Promise<void>) | null>(null);
  const clearRef = useRef<(() => void) | null>(null);
  const chatGenRef = useRef<(() => Promise<void>) | null>(null);

  // Welcome on first open; focus input.
  useEffect(() => {
    if (!open) return;
    setMessages((m) => (m.length === 0 ? [{ role: 'assistant', html: WELCOME }] : m));
    const t = setTimeout(() => inputRef.current?.focus(), 380);
    return () => clearTimeout(t);
  }, [open]);

  const panelRef = useOverlay(open, onClose, { lockScroll: true, closeOnOutsideClick: false });

  useEffect(() => {
    if (msgsRef.current) msgsRef.current.scrollTop = msgsRef.current.scrollHeight;
  }, [messages, typing]);

  const patchLast = useCallback((patch: Partial<StellaMessage>) => {
    setMessages((m) => {
      const c = [...m];
      c[c.length - 1] = { ...c[c.length - 1], ...patch };
      return c;
    });
  }, []);

  const streamAssistant = useCallback(async (html: string, actions: StellaAction[]) => {
    setTyping(false);
    setMessages((m) => [...m, { role: 'assistant', html: '', cursor: true }]);
    const tokens = tokenize(html);
    let buf = '';
    for (const tok of tokens) {
      if (tok.type === 'tag') {
        buf += tok.value;
        patchLast({ html: buf });
      } else {
        for (let j = 0; j < tok.value.length; j += 4) {
          buf += tok.value.substring(j, j + 4);
          patchLast({ html: buf });
          await wait(7 + Math.random() * 9);
        }
      }
    }
    patchLast({ cursor: false, actions: actions && actions.length ? actions : null });
  }, [patchLast]);

  const pushUser = useCallback((html: string) => {
    setMessages((m) => [...m, { role: 'user', html }]);
  }, []);

  const send = useCallback(async (raw?: string) => {
    const text = (raw ?? input).trim();
    if (!text || streamRef.current) return;
    setInput('');
    setAllSugs(false);
    pushUser(`<div>${esc(text)}</div>`);
    STELLA_HISTORY.push({ role: 'user', content: text });
    streamRef.current = true;
    setBusy(true);
    setTyping(true);

    let r: StellaRespondResult | null = null;
    let live = false;

    {
      try {
        const res = await pbAuthedFetch('/api/ai', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            system: STELLA_LIVE_SYSTEM,
            messages: STELLA_HISTORY.slice(-10).map((m) => ({ role: m.role, content: m.content })),
          }),
        });
        if (res.ok) {
          const d = await res.json().catch(() => null);
          if (d && typeof d.text === 'string' && d.text.trim()) {
            r = { text: mdLite(d.text.trim()), actions: [] };
            live = true;
          }
        }
      } catch {
        /* network error — canned fallback below */
      }
    }

    if (!r) {
      await wait(600 + Math.random() * 400);
      r = respond(text);
    }

    await streamAssistant(r.text, r.actions);
    STELLA_HISTORY.push({ role: 'assistant', content: r.text });
    streamRef.current = false;
    setBusy(false);

    const audit = winFn('appendActivityRow');
    if (audit) {
      (audit as (row: Record<string, unknown>) => void)({
        status: 'info',
        label: 'Stella · responded',
        detail: text.substring(0, 72),
        dur: live ? 'Atlas · live' : 'Atlas · ~1s',
      });
    }
  }, [input, pushUser, streamAssistant]);

  sendRef.current = send;

  const clear = useCallback(() => {
    STELLA_HISTORY.length = 0;
    setMessages([{ role: 'assistant', html: WELCOME }]);
  }, []);
  clearRef.current = clear;

  const chatGenerateExampleTicket = useCallback(async () => {
    await wait(380);
    pushUser("I just noticed elevated error rates on a critical service. Can you investigate?");
    setTyping(true);
    await wait(900);
    const responseHtml = "I'll investigate. Based on current telemetry, this looks like it needs immediate attention. Let me pull up the relevant sources and check for recent changes that might correlate.";
    await streamAssistant(responseHtml, [
      { label: 'Open Outages', kind: 'primary', onClick: () => goPage('outages') },
      { label: 'Discard', onClick: () => { /* no-op */ } },
    ]);
  }, [pushUser, streamAssistant]);
  chatGenRef.current = chatGenerateExampleTicket;

  // Imperative deep-link API
  useEffect(() => {
    window.stellaOpenEntity = stellaOpenEntity;
    window.askStella = (prompt: string) => {
      const closeMo = winFn('closeMo');
      if (closeMo) (closeMo as () => void)();
      setTimeout(() => sendRef.current?.(prompt), 380);
    };
    window.stellaAsk = (prompt: string) => sendRef.current?.(prompt);
    window.chatPrefill = (text: string) => { setInput(text); inputRef.current?.focus(); };
    window.chatClear = () => clearRef.current?.();
    window.chatSend = () => sendRef.current?.();
    window.chatGenerateExampleTicket = () => chatGenRef.current?.();
    return () => {
      (['stellaOpenEntity', 'askStella', 'stellaAsk', 'chatPrefill', 'chatClear', 'chatSend', 'chatGenerateExampleTicket'] as const).forEach((k) => {
        delete window[k];
      });
    };
  }, []);

  return (
    <>
      <div
        className={`scrim${open ? ' is-on' : ''}`}
        id="stella-backdrop"
        onClick={onClose}
      />
      <aside
        className={`stella-panel drawer${open ? ' is-on' : ''}`}
        id="stella-panel"
        aria-hidden={!open}
        ref={panelRef as React.RefObject<HTMLElement>}
      >
        <div className="stella-panel-inner">
          <div className="stella-header">
            <div className="stella-title">
              <div className="stella-orb" />
              <div>
                <div className="stella-name">Stella</div>
                <div className="stella-sub">
                  <span className="stella-livedot" />
                  <span>Atlas AI · Online</span>
                </div>
              </div>
            </div>
            <div className="stella-header-actions">
              <button
                className="ibtn"
                onClick={clear}
                title="Clear conversation"
                aria-label="Clear conversation"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 6h18" />
                  <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                  <path d="M10 11v6" />
                  <path d="M14 11v6" />
                </svg>
              </button>
              <button
                className="ibtn"
                onClick={onClose}
                title="Close"
                aria-label="Close"
                style={{ marginLeft: 2 }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                  <path d="M18 6L6 18" />
                  <path d="M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="stella-messages" id="stella-messages" ref={msgsRef}>
            {messages.map((m, i) => {
              const cls = `stella-msg ${m.role}${m.cursor ? ' stella-msg-cursor' : ''}`;
              if (m.actions && m.actions.length) {
                return (
                  <div key={i} className={cls}>
                    <div dangerouslySetInnerHTML={{ __html: m.html }} />
                    <div className="msg-actions">
                      {m.actions.map((a, j) => (
                        <button
                          key={j}
                          className={`msg-action ${a.kind || ''}`}
                          onClick={a.onClick}
                          dangerouslySetInnerHTML={{ __html: a.label }}
                        />
                      ))}
                    </div>
                  </div>
                );
              }
              return (
                <div
                  key={i}
                  className={cls}
                  dangerouslySetInnerHTML={{ __html: m.html }}
                />
              );
            })}
            {typing && (
              <div className="stella-typing" id="stella-thinking">
                <span className="dot" />
                <span className="dot" />
                <span className="dot" />
              </div>
            )}
          </div>

          <div className="stella-suggestions" id="stella-suggestions">
            {allSugs
              ? SUGGESTIONS.map(([group, items]) => (
                  <React.Fragment key={group}>
                    <div className="stella-sug-group">{group}</div>
                    {items.map((s) => (
                      <button type="button" key={s} className="stella-sug" onClick={() => send(s)}>
                        {s}
                      </button>
                    ))}
                  </React.Fragment>
                ))
              : QUICK_SUGGESTIONS.map((s) => (
                  <button type="button" key={s} className="stella-sug" onClick={() => send(s)}>
                    {s}
                  </button>
                ))}
            <button
              type="button"
              className="stella-sug stella-sug-toggle"
              aria-expanded={allSugs}
              aria-controls="stella-suggestions"
              onClick={() => setAllSugs((v) => !v)}
            >
              {allSugs ? 'Show fewer ▴' : 'All suggestions ▾'}
            </button>
          </div>

          <div className="stella-input-area">
            <div className="stella-input-wrap">
              <textarea
                className="stella-input"
                id="stella-input"
                rows={1}
                ref={inputRef}
                value={input}
                placeholder="Ask Stella about sources, CIs, risks, outages, or anything in the infrastructure..."
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
              />
              <button
                className="ibtn"
                id="stella-send-btn"
                onClick={() => send()}
                aria-label="Send"
                disabled={busy}
                title={busy ? 'Stella is responding…' : 'Send'}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              </button>
            </div>
            <div className="stella-input-hint">
              <span>
                Press <kbd>Enter</kbd> to send · <kbd>Shift+Enter</kbd> new line
              </span>
              <span className="stella-mode" id="stella-mode-badge">
                ● Streaming
              </span>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}

export default StellaPanel;
